/**
 * useS3Content Hook
 *
 * Manages loading and saving TipTap content to/from S3 via presigned URLs.
 * Used by SpecEditor and CardEditor components for content persistence.
 *
 * Architecture:
 * - Content is stored in S3 as JSON (TipTap JSONContent format)
 * - Backend provides presigned URLs for secure read/write access
 * - Hook handles the full lifecycle: get presigned URL -> fetch/put content -> confirm save
 * - Supports migration from legacy database-stored content
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { JSONContent } from '@tiptap/react';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// =============================================================================
// Types
// =============================================================================

export interface UseS3ContentOptions {
  /** Content type: specification or standard */
  type: 'specification' | 'standard';
  /** Entity ID (Specification_ID or Standard_ID) */
  id: string;
  /** Optional error callback */
  onError?: (error: string) => void;
  /** Optional success callback for save operations */
  onSaveSuccess?: () => void;
  /** Auto-load content on mount (default: true) */
  autoLoad?: boolean;
}

export interface UseS3ContentReturn {
  /** Current content (null if not loaded) */
  content: JSONContent | null;
  /** Loading state for initial content fetch */
  loading: boolean;
  /** Saving state for PUT operations */
  saving: boolean;
  /** Error message (null if no error) */
  error: string | null;
  /** Whether this content needs migration from legacy storage */
  needsMigration: boolean;
  /** Load content from S3 */
  loadContent: () => Promise<JSONContent | null>;
  /** Save content to S3 */
  saveContent: (content: JSONContent) => Promise<boolean>;
  /** Content version for optimistic updates */
  contentVersion: number;
  /** Clear the current error */
  clearError: () => void;
  /** Reset hook state */
  reset: () => void;
}

interface ContentGetResponse {
  presigned_url: string;
  content_type: string;
  expires_in: number;
  needs_migration: boolean;
  legacy_content?: JSONContent;
  content_size_bytes?: number;
  last_modified?: string;
}

interface ContentPutResponse {
  presigned_url: string;
  content_type: string;
  expires_in: number;
  max_size_bytes: number;
}

interface ContentConfirmResponse {
  success: boolean;
  content_hash: string;
  content_size_bytes: number;
  version: number;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useS3Content({
  type,
  id,
  onError,
  onSaveSuccess,
  autoLoad = true
}: UseS3ContentOptions): UseS3ContentReturn {
  // State
  const [content, setContent] = useState<JSONContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsMigration, setNeedsMigration] = useState(false);
  const [contentVersion, setContentVersion] = useState(0);

  // Refs for preventing stale closures and duplicate requests
  const loadingRef = useRef(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);

  // Get the appropriate endpoint and param name based on content type
  const getEndpoints = useCallback(() => {
    if (type === 'specification') {
      return {
        get: API_ENDPOINTS.SPECIFICATIONS.CONTENT_GET,
        put: API_ENDPOINTS.SPECIFICATIONS.CONTENT_PUT,
        paramName: 'specId'
      };
    } else {
      return {
        get: API_ENDPOINTS.STANDARDS.CONTENT_GET,
        put: API_ENDPOINTS.STANDARDS.CONTENT_PUT,
        paramName: 'cardId'
      };
    }
  }, [type]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset state
  const reset = useCallback(() => {
    setContent(null);
    setLoading(false);
    setSaving(false);
    setError(null);
    setNeedsMigration(false);
    setContentVersion(0);
    loadingRef.current = false;
    savingRef.current = false;
  }, []);

  /**
   * Load content from S3
   * 1. Get presigned URL from backend
   * 2. Fetch content from S3 using presigned URL
   * 3. Handle legacy content migration
   */
  const loadContent = useCallback(async (): Promise<JSONContent | null> => {
    // Prevent duplicate requests
    if (loadingRef.current) {
      return content;
    }

    if (!id) {
      const errorMsg = 'Cannot load content: missing entity ID';
      setError(errorMsg);
      onError?.(errorMsg);
      return null;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const endpoints = getEndpoints();

      // Step 1: Get presigned URL from backend (using query parameter)
      const presignedResponse = await Make_Authorized_API_Call<ContentGetResponse>(
        endpoints.get,
        'GET',
        undefined,
        { params: { [endpoints.paramName]: id } }
      );

      if (!mountedRef.current) return null;

      if (!isSuccessResponse(presignedResponse)) {
        throw new Error(presignedResponse.message || 'Failed to get content URL');
      }

      const responseData = presignedResponse.data.Records[0];
      if (!responseData) {
        throw new Error('No content data returned from server');
      }

      // Handle legacy content that needs migration
      if (responseData.needs_migration && responseData.legacy_content) {
        setNeedsMigration(true);
        setContent(responseData.legacy_content);
        setContentVersion(prev => prev + 1);
        return responseData.legacy_content;
      }

      // Step 2: Fetch content from S3 using presigned URL
      if (responseData.presigned_url) {
        const s3Response = await fetch(responseData.presigned_url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!mountedRef.current) return null;

        if (!s3Response.ok) {
          // If S3 returns 404, content might not exist yet (new document)
          if (s3Response.status === 404) {
            setContent(null);
            setNeedsMigration(false);
            return null;
          }
          throw new Error(`Failed to fetch content from storage: ${s3Response.status}`);
        }

        const jsonContent: JSONContent = await s3Response.json();
        setContent(jsonContent);
        setNeedsMigration(false);
        setContentVersion(prev => prev + 1);
        return jsonContent;
      }

      // No presigned URL and no legacy content means empty document
      setContent(null);
      setNeedsMigration(false);
      return null;

    } catch (err) {
      if (!mountedRef.current) return null;

      const errorMsg = err instanceof Error ? err.message : 'Failed to load content';
      setError(errorMsg);
      onError?.(errorMsg);
      console.error('[useS3Content] Load error:', err);
      return null;

    } finally {
      if (mountedRef.current) {
        setLoading(false);
        loadingRef.current = false;
      }
    }
  }, [id, content, getEndpoints, onError]);

  /**
   * Save content to S3
   * 1. Get presigned PUT URL from backend
   * 2. PUT content to S3
   * 3. Confirm save with backend (updates metadata, clears needs_migration)
   */
  const saveContent = useCallback(async (newContent: JSONContent): Promise<boolean> => {
    // Prevent duplicate save requests
    if (savingRef.current) {
      return false;
    }

    if (!id) {
      const errorMsg = 'Cannot save content: missing entity ID';
      setError(errorMsg);
      onError?.(errorMsg);
      return false;
    }

    if (!newContent) {
      const errorMsg = 'Cannot save content: content is empty';
      setError(errorMsg);
      onError?.(errorMsg);
      return false;
    }

    savingRef.current = true;
    setSaving(true);
    setError(null);

    try {
      const endpoints = getEndpoints();
      const contentString = JSON.stringify(newContent);
      const contentBytes = new TextEncoder().encode(contentString).length;

      // Step 1: Get presigned PUT URL from backend (using query parameter)
      const presignedResponse = await Make_Authorized_API_Call<ContentPutResponse>(
        endpoints.put,
        'POST',
        {
          content_size_bytes: contentBytes,
          content_type: 'application/json'
        },
        { params: { [endpoints.paramName]: id } }
      );

      if (!mountedRef.current) return false;

      if (!isSuccessResponse(presignedResponse)) {
        throw new Error(presignedResponse.message || 'Failed to get upload URL');
      }

      const uploadData = presignedResponse.data.Records[0];
      if (!uploadData?.presigned_url) {
        throw new Error('No upload URL returned from server');
      }

      // Check content size limit
      if (contentBytes > uploadData.max_size_bytes) {
        throw new Error(`Content too large: ${contentBytes} bytes exceeds maximum of ${uploadData.max_size_bytes} bytes`);
      }

      // Step 2: PUT content to S3
      const s3Response = await fetch(uploadData.presigned_url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: contentString
      });

      if (!mountedRef.current) return false;

      if (!s3Response.ok) {
        throw new Error(`Failed to upload content: ${s3Response.status} ${s3Response.statusText}`);
      }

      // Step 3: Confirm save with backend (same endpoint with confirm_save: true)
      const confirmResponse = await Make_Authorized_API_Call<ContentConfirmResponse>(
        endpoints.put,
        'POST',
        {
          confirm_save: true,
          content_size_bytes: contentBytes,
          s3_etag: s3Response.headers.get('ETag')?.replace(/"/g, '') || undefined
        },
        { params: { [endpoints.paramName]: id } }
      );

      if (!mountedRef.current) return false;

      if (!isSuccessResponse(confirmResponse)) {
        // Content was uploaded but confirmation failed
        // This is a partial success - log warning but don't fail
        console.warn('[useS3Content] Content uploaded but confirmation failed:', confirmResponse.message);
      }

      // Update local state
      setContent(newContent);
      setNeedsMigration(false);
      setContentVersion(prev => prev + 1);
      onSaveSuccess?.();

      return true;

    } catch (err) {
      if (!mountedRef.current) return false;

      const errorMsg = err instanceof Error ? err.message : 'Failed to save content';
      setError(errorMsg);
      onError?.(errorMsg);
      console.error('[useS3Content] Save error:', err);
      return false;

    } finally {
      if (mountedRef.current) {
        setSaving(false);
        savingRef.current = false;
      }
    }
  }, [id, getEndpoints, onError, onSaveSuccess]);

  // Auto-load on mount if enabled
  useEffect(() => {
    mountedRef.current = true;

    if (autoLoad && id) {
      loadContent();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [id, autoLoad]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: loadContent is intentionally excluded to prevent infinite loops
  // The dependency on `id` handles reloading when the entity changes

  return {
    content,
    loading,
    saving,
    error,
    needsMigration,
    loadContent,
    saveContent,
    contentVersion,
    clearError,
    reset
  };
}

export default useS3Content;
